import $ from 'jquery';
import Keyboard from 'styleguide/modules/keyboard';

const STORY_BAR_CLASS = 'revolve-magazine__story-bar';

const SELECTORS = {
  root: '.js-revolve-magazine',
  toggle: '.js-revolve-magazine-toggle',
  contents: '.js-revolve-magazine-contents',
  close: '.js-revolve-magazine-close',
  // Structural to the component rather than js- hooks: the bars are addressed
  // by position inside the magazine root, which already scopes them.
  spread: '.revolve-magazine__spread',
  storyBar: `.${STORY_BAR_CLASS}`,
};

const OPEN_CLASS = 'is-open';
const ROOT_OPEN_CLASS = 'revolve-magazine--contents-open';
const ROOT_STORY_BARS_CLASS = 'revolve-magazine--story-bars';
const LIVE_BAR_CLASS = `${STORY_BAR_CLASS}--live`;
const EMPTY_CLASS = 'is-empty';

// Sub-pixel slack, so a spread counts as under the bar the moment its top
// reaches the line instead of a fraction of a pixel later.
const DOCK_EPSILON = 1;

// The shell's skip links, keyed by the hash they ship with. The magazine has
// no search box, and its main/footer regions are not the shell's.
const SKIP_LINKS = {
  hide: ['#search_term_new'],
  retarget: {
    '#main-content': '#main-content-magazine',
    '#site-footer': '#site-footer-magazine',
  },
};

/**
 * Progressive enhancement for the magazine page: turns the "contents" anchor
 * into an off-canvas drawer toggle. The spreads and their links are
 * server-rendered and handled with native CSS; the per-story title bars are
 * still natively sticky but their visibility is driven by `initStoryBars`.
 * While the drawer is open, Tab is kept inside it via the styleguide Keyboard
 * module. That pulls in jQuery (an external on the window), so this bundle
 * has to load after it - it is deferred behind chrome.js.
 *
 * @param {ParentNode} [scope=document] Where to look for the magazine root.
 * @return {Object|null} Controller API, or null when there is no magazine.
 */
export function initRevolveMagazine(scope = document) {
  const root = scope.querySelector(SELECTORS.root);
  if (!root) return null;

  const toggle = root.querySelector(SELECTORS.toggle);
  const contents = root.querySelector(SELECTORS.contents);
  const closeControl = root.querySelector(SELECTORS.close);

  let open = false;

  function setOpen(nextOpen) {
    const wasOpen = open;
    open = Boolean(nextOpen);

    if (contents) contents.classList.toggle(OPEN_CLASS, open);
    root.classList.toggle(ROOT_OPEN_CLASS, open);
    if (toggle) toggle.setAttribute('aria-expanded', String(open));

    if (open) {
      if (!wasOpen && contents) Keyboard.trapFocus($(contents));
      if (closeControl) closeControl.focus();
    } else if (wasOpen && contents) {
      // Hand focus back to the toggle only if it was still inside the drawer,
      // so a dismissal from elsewhere on the page keeps its own focus.
      const focusWasInside = toggle && contents.contains(document.activeElement);
      Keyboard.releaseFocus($(contents), focusWasInside ? $(toggle) : null);
    }
  }

  function onToggleClick(event) {
    event.preventDefault();
    setOpen(!open);
  }

  function onCloseClick(event) {
    event.preventDefault();
    setOpen(false);
  }

  function onContentsClick(event) {
    const link = event.target.closest('a[href^="#"]');
    if (link && !link.matches(SELECTORS.close)) setOpen(false);
  }

  function onKeydown(event) {
    if (open && (event.key === 'Escape' || event.key === 'Esc')) setOpen(false);
  }

  function onRootClick(event) {
    // Dismiss on the scrim. The scrim is the root's own ::before, so a click
    // on it hit-tests to the root element itself - and since the scrim covers
    // the viewport above everything except the drawer, a click that reports
    // the root as its target can only have landed there.
    if (open && event.target === root) setOpen(false);
  }

  if (toggle) toggle.addEventListener('click', onToggleClick);
  if (closeControl) closeControl.addEventListener('click', onCloseClick);
  if (contents) contents.addEventListener('click', onContentsClick);
  root.addEventListener('click', onRootClick);
  document.addEventListener('keydown', onKeydown);

  return {
    open: () => setOpen(true),
    close: () => setOpen(false),
    isOpen: () => open,
    destroy() {
      if (contents) Keyboard.releaseFocus($(contents));
      if (toggle) toggle.removeEventListener('click', onToggleClick);
      if (closeControl) closeControl.removeEventListener('click', onCloseClick);
      if (contents) contents.removeEventListener('click', onContentsClick);
      root.removeEventListener('click', onRootClick);
      document.removeEventListener('keydown', onKeydown);
    },
  };
}

/**
 * One live title bar for the whole issue. The bars the server renders into
 * each spread leave the flow once this runs (see _rm-spread.scss) and a single
 * bar pinned under the banner shows the title of whichever story sits beneath
 * its bottom edge, swapping its content in place as the reader scrolls.
 * Nothing pushes anything: a new story simply changes what the bar says, and a
 * story with no bar of its own (the editor's letter) blanks it.
 *
 * Reading rects on scroll rather than using IntersectionObserver sentinels: an
 * issue is a couple of dozen spreads, the work is one rect per spread behind a
 * rAF, and "which spread is under the bar" stays a single readable expression.
 *
 * @param {ParentNode} [scope=document] Where to look for the magazine root.
 * @return {Object|null} Controller API, or null when there are no story bars.
 */
export function initStoryBars(scope = document) {
  const root = scope.querySelector(SELECTORS.root);
  if (!root) return null;

  // Every spread, with or without a bar: a story that has none still has to
  // blank the live bar while it is the one being read.
  const spreads = Array.prototype.slice.call(root.querySelectorAll(SELECTORS.spread))
    .map(spread => ({ spread, bar: spread.querySelector(SELECTORS.storyBar) }));

  if (!spreads.some(pair => pair.bar)) return null;

  const live = document.createElement('div');
  live.className = `${STORY_BAR_CLASS} ${LIVE_BAR_CLASS} ${EMPTY_CLASS}`;
  // Ahead of the first spread, so it comes before the pages in the tab order.
  spreads[0].spread.parentNode.insertBefore(live, spreads[0].spread);

  // Claim the behaviour only once it is known to be wired up. Without this
  // class the stylesheet keeps each spread's own bar, sticky, which is the
  // right place for a page whose JS never arrives to end up.
  root.classList.add(ROOT_STORY_BARS_CLASS);

  let current = null;

  /**
   * Puts `pair`'s bar content into the live bar, or blanks it when the story
   * under the bar has none. Copies rather than moves: each section's
   * aria-labelledby points at its own heading, so that has to stay where it
   * is. Ids come off the copy so the page never holds two of the same.
   * @param {Object|null} pair The spread now under the bar, if any.
   */
  function show(pair) {
    if (pair === current) return;
    current = pair;

    while (live.firstChild) live.removeChild(live.firstChild);

    if (!pair || !pair.bar) {
      live.classList.add(EMPTY_CLASS);
      return;
    }

    Array.prototype.forEach.call(pair.bar.children, (child) => {
      const copy = child.cloneNode(true);
      copy.removeAttribute('id');
      Array.prototype.forEach.call(copy.querySelectorAll('[id]'), (el) => {
        el.removeAttribute('id');
      });
      live.appendChild(copy);
    });
    live.classList.remove(EMPTY_CLASS);
  }

  function render() {
    // The bar is fixed under the banner and only ever blanked with visibility,
    // so its box is always there to measure. Its bottom edge is the line: the
    // story under the bar is the one whose spread straddles it.
    const line = live.getBoundingClientRect().bottom;

    // Every rect is read before anything is written, so a frame's work is a
    // single layout pass.
    const rects = spreads.map(({ spread }) => spread.getBoundingClientRect());
    let under = null;
    rects.forEach((rect, index) => {
      if (rect.top <= line + DOCK_EPSILON && rect.bottom > line) under = spreads[index];
    });

    show(under);
  }

  let frame = null;

  function schedule() {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      render();
    });
  }

  render();

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);

  return {
    /** Re-reads the layout and repaints, for use after a layout change. */
    refresh: render,
    destroy() {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      root.classList.remove(ROOT_STORY_BARS_CLASS);
      if (live.parentNode) live.parentNode.removeChild(live);
    },
  };
}

/**
 * Aims the shell's skip links at the magazine's own landmarks. The header
 * markup is shared with every other page and cannot be edited for this one,
 * so the links are adjusted after the fact: the search skip link is hidden
 * and the main/footer ones are re-pointed. The focusTo plugin reads the hash
 * at click time, so a changed href is all it needs. A link is only re-pointed
 * when its magazine target exists, so it never ends up skipping to nowhere.
 *
 * @param {ParentNode} [scope=document] Where to look for the skip links.
 * @return {Object} Controller API whose `destroy` puts the links back.
 */
export function initSkipLinks(scope = document) {
  const hidden = [];
  const retargeted = [];

  SKIP_LINKS.hide.forEach((hash) => {
    Array.prototype.forEach.call(scope.querySelectorAll(`a[href="${hash}"]`), (link) => {
      // `hidden` says what is meant; the inline display is what makes it
      // stick, since the .btn display rule would beat the attribute alone.
      link.setAttribute('hidden', '');
      link.style.setProperty('display', 'none');
      hidden.push(link);
    });
  });

  Object.keys(SKIP_LINKS.retarget).forEach((from) => {
    const to = SKIP_LINKS.retarget[from];
    if (!scope.querySelector(to)) return;

    Array.prototype.forEach.call(scope.querySelectorAll(`a[href="${from}"]`), (link) => {
      link.setAttribute('href', to);
      retargeted.push({ link, from });
    });
  });

  return {
    destroy() {
      hidden.forEach((link) => {
        link.removeAttribute('hidden');
        link.style.removeProperty('display');
      });
      retargeted.forEach(({ link, from }) => link.setAttribute('href', from));
    },
  };
}

/**
 * Runs `callback` once the DOM is parsed. Fires immediately if it already is.
 * @param {Function} callback
 */
export function onDomReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
    return;
  }
  callback();
}



// WEBPACK FOOTER //
// ./src/scripts/modules/revolve-magazine.js